import { slugSegmentoFiltro } from './FunctionsGlobals';
import { tipoPorIdHomonimo } from './tiposHomonimos';

// O link do TÍTULO (e do botão "Ver todos") de um carrossel da home tem que levar o
// visitante para a MESMA busca que montou os cards. Até 11/09 ele usava a transação
// priorizada do SITE (`site.transacoes[0]`) e ignorava `venda`/`aluguel` e os filtros do
// próprio carrossel: num site de venda+aluguel, o carrossel "Para alugar" caía na listagem
// de VENDA (cs2636 "Para Alugar em Itaquera e Zona Leste", e mais 4 licenças).
//
// A rota de destino é /imoveis/{model}/{city}/{district}/{company}/{type}: `model` só aceita
// venda|aluguel|temporada (qualquer outro valor cai em venda em silêncio) e cidade/bairro/tipo
// são casados por SLUG contra os nomes do tenant (`segmentoFiltroCasaNome`), por isso os
// segmentos saem daqui pelo MESMO `slugSegmentoFiltro` que a listagem usa na volta.

type DepsCarrossel = {
    site?: any;
    items?: any[];   // carrossel[1] — imóveis já carregados (trazem cidade/bairro/tipo com id e nome)
    cidades?: any[]; // allDataHome.cidades
    tipos?: any[];   // allDataHome.tipos
};

const MODELOS_DA_ROTA = ['venda', 'aluguel', 'temporada'];

const idPositivo = (valor: any): number => {
    const numero = Number(valor);
    return Number.isFinite(numero) && numero > 0 ? numero : 0;
};

// as flags do carrossel chegam como 1/0 do MySQL ou já como boolean pelo GraphQL
const ligado = (valor: any): boolean => valor === true || idPositivo(valor) > 0;

// O href de sempre: transação priorizada do site, sem filtro nenhum. Continua sendo a saída
// quando não dá para derivar nada — assim o pior caso do link novo é o link de hoje.
export function urlCarrosselPadrao(site: any): string {
    const transacao = String(site?.transacoes?.[0] ?? '').toLowerCase();
    return transacao ? `/imoveis/${transacao}/-/-/-/-?ordem=desc-inclusao` : '';
}

const nomePorId = (id: number, lista: any): string => {
    if (!id || !Array.isArray(lista)) return '';
    const achado = lista.find((item: any) => idPositivo(item?.id) === id) || tipoPorIdHomonimo(lista, id);
    return String(achado?.nome ?? '').trim();
};

const nomeNosItens = (id: number, items: any[], campo: 'cidade' | 'tipo'): string => {
    if (!id) return '';
    const achado = items.find((item: any) => idPositivo(item?.[campo]?.id) === id);
    return String(achado?.[campo]?.nome ?? '').trim();
};

// venda XOR aluguel decide sozinho; com os dois (ou nenhum) marcados o carrossel não promete
// transação, então vale a do site — o mesmo critério do `venda`/`aluguel` de properties_carrossel.
const transacaoDoCarrossel = (config: any, site: any): string => {
    const venda = ligado(config?.venda);
    const aluguel = ligado(config?.aluguel);
    if (venda !== aluguel) return aluguel ? 'aluguel' : 'venda';

    const doSite = String(site?.transacoes?.[0] ?? '').toLowerCase();
    return MODELOS_DA_ROTA.includes(doSite) ? doSite : '';
};

export function montaUrlCarrossel(config: any, deps: DepsCarrossel): string {
    const manual = String(config?.url_red ?? '').trim();
    if (manual) return manual; // "URL do botão Ver mais" do painel continua mandando

    const padrao = urlCarrosselPadrao(deps?.site);

    // Só o carrossel `personalizado` filtra: as buscas de `top_10` (imoveis_destaque) e
    // `novidades_semana` (imoveis_novidades) não recebem nenhum campo da configuração, então
    // derivar filtro no link prometeria um recorte que os cards não fazem.
    if (!ligado(config?.personalizado)) return padrao;

    const model = transacaoDoCarrossel(config, deps?.site);
    if (!model) return padrao;

    const items = Array.isArray(deps?.items) ? deps.items : [];
    const idCidade = idPositivo(config?.id_cidade);
    const idBairro = idPositivo(config?.id_bairro);
    const idTipo = idPositivo(config?.id_tipo);

    // O bairro conhece a cidade dele: quando o carrossel filtra só por bairro, a cidade sai do
    // próprio imóvel, senão a listagem descarta o bairro (ela só resolve bairro dentro da cidade).
    const itemDoBairro = idBairro
        ? items.find((item: any) => idPositivo(item?.bairro?.id) === idBairro)
        : undefined;

    let nomeCidade = nomePorId(idCidade, deps?.cidades) || nomeNosItens(idCidade, items, 'cidade');
    if (!nomeCidade && itemDoBairro) nomeCidade = String(itemDoBairro?.cidade?.nome ?? '').trim();

    const nomeBairro = nomeCidade && itemDoBairro ? String(itemDoBairro?.bairro?.nome ?? '').trim() : '';
    const nomeTipo = nomePorId(idTipo, deps?.tipos) || nomeNosItens(idTipo, items, 'tipo');

    // slugSegmentoFiltro devolve '-' para nome vazio: filtro que não resolveu vira "sem filtro"
    // (link mais largo, igual ao de hoje), nunca um slug que a listagem descartaria em silêncio.
    const city = slugSegmentoFiltro(nomeCidade, deps?.site?.id);
    const district = nomeCidade ? slugSegmentoFiltro(nomeBairro, deps?.site?.id) : '-';
    const type = slugSegmentoFiltro(nomeTipo, deps?.site?.id);

    // ?ordem=desc-inclusao espelha o `ordenar: Inclusao, ordem: desc` da busca do carrossel
    return `/imoveis/${model}/${city}/${district}/-/${type}?ordem=desc-inclusao`;
}
